import React, { FC, useEffect, useState } from 'react'
import EditText from 'blocks.simple/editText/editText'
import Icon from 'blocks.simple/icon/icon'
import translate from 'core/translate'
import { cn } from 'ethcss'
import { Button } from 'molecules/Button'
import { useTemplateEditorContext } from '../hooks/templateEditorContext'
import { TemplateElement } from '../templateEditor.types'
import { inspectorFieldStyles as styles } from './templateEditor__inspector.jcss'
import { TemplateEditorNumberStepper } from './templateEditor__numberStepper'

type TemplateEditorInspectorElementSettingsProps = {
    element: TemplateElement
}

export const TemplateEditorInspectorElementSettings: FC<TemplateEditorInspectorElementSettingsProps> = ({
    element,
}) => {
    const {
        toggleLock,
        toggleHidden,
        groupSelection,
        ungroupSelection,
        selectedIds,
        duplicateSelection,
        setElementRotate,
        setElementRect,
        theme,
        startImageReplaceMode,
        resetImageToNaturalSize,
        fitImageToAxis,
        setImageObjectFit,
    } = useTemplateEditorContext()
    const [rotateDraft, setRotateDraft] = useState(String(Math.round(element.rotate ?? 0)))
    const locked = Boolean(element.locked)
    const objectFit = element.type === 'image' ? element.objectFit || 'contain' : 'contain'
    const canFitNatural = element.type === 'image' && Boolean(element.naturalW && element.naturalH)

    useEffect(() => {
        setRotateDraft(String(Math.round(element.rotate ?? 0)))
    }, [element.id, element.rotate])

    return (
        <div className={styles.fieldGroup}>
            <div className={styles.fieldGroupLabel}>{translate('position')}</div>
            <div className={styles.fieldRow}>
                <TemplateEditorNumberStepper
                    label="X"
                    value={Math.round(element.x)}
                    disabled={locked}
                    onChange={(x) => setElementRect(element.id, { x })}
                />
                <TemplateEditorNumberStepper
                    label="Y"
                    value={Math.round(element.y)}
                    disabled={locked}
                    onChange={(y) => setElementRect(element.id, { y })}
                />
            </div>
            <div className={styles.fieldRow}>
                <TemplateEditorNumberStepper
                    label={translate('templateEditorWidthShort')}
                    value={Math.round(element.w)}
                    min={theme.minElSize}
                    disabled={locked}
                    onChange={(w) => setElementRect(element.id, { w })}
                />
                <TemplateEditorNumberStepper
                    label={translate('templateEditorHeightShort')}
                    value={Math.round(element.h)}
                    min={theme.minElSize}
                    disabled={locked}
                    onChange={(h) => setElementRect(element.id, { h })}
                />
            </div>
            <EditText
                label={translate('rotate')}
                value={rotateDraft}
                onChange={(value: string) => setRotateDraft(value)}
                onBlur={() => {
                    const parsed = Number(rotateDraft)
                    if (!Number.isFinite(parsed)) {
                        setRotateDraft(String(Math.round(element.rotate ?? 0)))
                        return
                    }
                    const next = Math.round(parsed)
                    setRotateDraft(String(next))
                    if (next !== Math.round(element.rotate ?? 0)) {
                        setElementRotate(element.id, next)
                    }
                }}
                rounded="full_mini"
            />
            {element.type === 'image' && (
                <>
                    <div className={styles.fieldRow} />
                    <div className={styles.fieldGroupLabel}>{translate('templateEditorImage')}</div>
                    <div className={styles.fieldGroupLabel}>{translate('templateEditorImageFit')}</div>
                    <div className={styles.segmentRow}>
                        <button
                            type="button"
                            className={cn(styles.segmentButton, objectFit === 'contain' && styles.segmentButtonActive)}
                            disabled={locked}
                            title={translate('templateEditorFitContain')}
                            onClick={() => setImageObjectFit(element.id, 'contain')}
                        >
                            <Icon type="frames" size={14} color="currentColor" />
                            <span>{translate('templateEditorFitContain')}</span>
                        </button>
                        <button
                            type="button"
                            className={cn(styles.segmentButton, objectFit === 'cover' && styles.segmentButtonActive)}
                            disabled={locked}
                            title={translate('templateEditorFitCover')}
                            onClick={() => setImageObjectFit(element.id, 'cover')}
                        >
                            <Icon type="crop" size={14} color="currentColor" />
                            <span>{translate('templateEditorFitCover')}</span>
                        </button>
                    </div>
                    <div className={styles.fieldGroupLabel}>{translate('templateEditorImageFrame')}</div>
                    <div className={styles.imageToolGrid}>
                        <button
                            type="button"
                            className={styles.imageTool}
                            disabled={locked || !canFitNatural}
                            title={translate('templateEditorFitToWidth')}
                            onClick={() => fitImageToAxis(element.id, 'width')}
                        >
                            <Icon type="fitWidth" size={14} color="currentColor" />
                            <span>{translate('templateEditorFitToWidthShort')}</span>
                        </button>
                        <button
                            type="button"
                            className={styles.imageTool}
                            disabled={locked || !canFitNatural}
                            title={translate('templateEditorFitToHeight')}
                            onClick={() => fitImageToAxis(element.id, 'height')}
                        >
                            <Icon type="fitHeight" size={14} color="currentColor" />
                            <span>{translate('templateEditorFitToHeightShort')}</span>
                        </button>
                        <button
                            type="button"
                            className={styles.imageTool}
                            disabled={locked || !element.src}
                            title={translate('templateEditorOriginalSize')}
                            onClick={() => resetImageToNaturalSize(element.id)}
                        >
                            <Icon type="maximize" size={14} color="currentColor" />
                            <span>{translate('templateEditorOriginalSizeShort')}</span>
                        </button>
                    </div>
                    <button
                        type="button"
                        className={styles.imageReplace}
                        disabled={locked}
                        title={translate('templateEditorReplaceImage')}
                        onClick={() => startImageReplaceMode(element.id)}
                    >
                        <Icon type="switchArrows" size={14} color="currentColor" />
                        <span>{translate('templateEditorReplaceImage')}</span>
                    </button>
                </>
            )}
            <div className={styles.fieldRow} />
            <div className={styles.fieldGroupLabel}>{translate('templateEditorLayerControls')}</div>
            <div className={styles.actionsRow}>
                <Button className={styles.actionButton} mod="default" size="s" onClick={() => toggleLock([element.id])}>
                    {element.locked ? translate('templateEditorUnlock') : translate('templateEditorLock')}
                </Button>
                <Button
                    className={styles.actionButton}
                    mod="default"
                    size="s"
                    onClick={() => toggleHidden([element.id])}
                >
                    {element.hidden ? translate('templateEditorShow') : translate('templateEditorHide')}
                </Button>
            </div>
            <div className={styles.actionsRow}>
                <Button
                    className={styles.actionButton}
                    mod="default"
                    size="s"
                    disabled={selectedIds.length < 2}
                    onClick={() => groupSelection()}
                >
                    {translate('templateEditorGroup')}
                </Button>
                <Button
                    className={styles.actionButton}
                    mod="default"
                    size="s"
                    disabled={element.type !== 'group'}
                    onClick={() => ungroupSelection()}
                >
                    {translate('templateEditorUngroup')}
                </Button>
            </div>
            <Button className={styles.actionButtonFull} mod="fillBlack" size="s" onClick={() => duplicateSelection()}>
                {translate('templateEditorDuplicate')}
            </Button>
        </div>
    )
}
